"use client";

import { useCallback, useEffect, useRef, useState } from "react";

/* ==========================================================================
   TYPES
   ========================================================================== */

interface BootScreenProps {
  onComplete: () => void;
}

type Phase = "post" | "loader" | "fadeout" | "done";

/* ==========================================================================
   POST TEXT LINES
   ========================================================================== */

const POST_LINES = [
  { text: "SystemOS BIOS v2.4.1", delay: 300 },
  { text: "Copyright (c) 2026 Desktop UI Corp.", delay: 100 },
  { text: "", delay: 200 },
  { text: "CPU: React 18 Runtime Engine ............ OK", delay: 150 },
  { text: "Memory Test: 1048576K ................... OK", delay: 200 },
  {
    text: "Detecting storage ....................... Virtual FS [128MB]",
    delay: 180,
  },
  {
    text: "Detecting audio ........................ Xylophone Audio Interface",
    delay: 120,
  },
  {
    text: "Detecting display ...................... Canvas Rendering Engine",
    delay: 100,
  },
  { text: "", delay: 150 },
  { text: "Loading terminal.sys ................... OK", delay: 100 },
  { text: "Loading scout.sys ...................... OK", delay: 80 },
  { text: "Loading python.wasm .................... OK", delay: 120 },
  { text: "Loading codeeditor.sys ................. OK", delay: 80 },
  { text: "Loading arcade.drv ..................... OK", delay: 100 },
  { text: "", delay: 200 },
  { text: "All systems nominal. Starting SystemOS kernel...", delay: 400 },
];

/* Mobile: shorter sequence */
const POST_LINES_MOBILE = [
  { text: "SystemOS BIOS v2.4.1", delay: 200 },
  { text: "", delay: 100 },
  { text: "CPU: React 18 Runtime .... OK", delay: 120 },
  { text: "Memory: 1048576K ......... OK", delay: 150 },
  { text: "Storage: VFS [128MB] ..... OK", delay: 100 },
  { text: "", delay: 150 },
  { text: "Starting SystemOS...", delay: 300 },
];

const LOADER_DURATION = 1800;
const FADEOUT_DURATION = 600;

/* ==========================================================================
   COMPONENT
   ========================================================================== */

export function BootScreen({ onComplete }: BootScreenProps) {
  const [phase, setPhase] = useState<Phase>("post");
  const [visibleLines, setVisibleLines] = useState<number>(0);
  const [loaderProgress, setLoaderProgress] = useState(0);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const completedRef = useRef(false);
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  const finish = useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    onComplete();
  }, [onComplete]);

  // Reduced motion: skip entirely
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mq.matches) {
      finish();
    }
  }, [finish]);

  // Main sequence orchestrator
  useEffect(() => {
    if (completedRef.current) return;

    const timers = timersRef.current;
    const lines = isMobile ? POST_LINES_MOBILE : POST_LINES;

    // Phase 1: POST text lines
    let elapsed = 0;
    lines.forEach((line, i) => {
      elapsed += line.delay;
      const t = setTimeout(() => {
        setVisibleLines(i + 1);
      }, elapsed);
      timers.push(t);
    });

    // Transition to loader phase
    elapsed += 300;
    const loaderStart = elapsed;
    timers.push(
      setTimeout(() => {
        setPhase("loader");
      }, elapsed)
    );

    // Loader progress animation (stepped for realism)
    const steps = 20;
    for (let i = 1; i <= steps; i++) {
      const t = setTimeout(
        () => {
          setLoaderProgress(i / steps);
        },
        loaderStart + (LOADER_DURATION * i) / steps
      );
      timers.push(t);
    }

    // Transition to fadeout
    elapsed = loaderStart + LOADER_DURATION + 200;
    timers.push(
      setTimeout(() => {
        setPhase("fadeout");
      }, elapsed)
    );

    // Complete
    elapsed += FADEOUT_DURATION;
    timers.push(
      setTimeout(() => {
        finish();
      }, elapsed)
    );

    return () => {
      timers.forEach(clearTimeout);
      timersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount

  // Skip on click/keypress
  useEffect(() => {
    const handleSkip = () => {
      if (phase === "fadeout" || phase === "done") return;
      setPhase("fadeout");
      const t = setTimeout(finish, FADEOUT_DURATION);
      timersRef.current.push(t);
    };

    // Only allow skip after a brief moment so it feels intentional
    const enableTimer = setTimeout(() => {
      window.addEventListener("click", handleSkip);
      window.addEventListener("keydown", handleSkip);
    }, 500);

    return () => {
      clearTimeout(enableTimer);
      window.removeEventListener("click", handleSkip);
      window.removeEventListener("keydown", handleSkip);
    };
  }, [phase, finish]);

  if (phase === "done") return null;

  const lines = isMobile ? POST_LINES_MOBILE : POST_LINES;

  return (
    <div
      className="boot-screen"
      style={{
        opacity: phase === "fadeout" ? 0 : 1,
        transition: `opacity ${FADEOUT_DURATION}ms ease-in-out`,
      }}
    >
      {/* POST phase */}
      {phase === "post" && (
        <div className="boot-post">
          {lines.slice(0, visibleLines).map((line, i) => (
            <div key={i} className="boot-post-line">
              {line.text}
              {line.text === "" && <br />}
            </div>
          ))}
          <span className="boot-cursor">_</span>
        </div>
      )}

      {/* Loader phase */}
      {(phase === "loader" || phase === "fadeout") && (
        <div className="boot-loader">
          <div className="boot-logo">
            {/* Simple OS logo: stylized monitor icon */}
            <svg
              width="64"
              height="64"
              viewBox="0 0 64 64"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="boot-logo-icon"
            >
              <rect
                x="8"
                y="6"
                width="48"
                height="36"
                rx="4"
                stroke="currentColor"
                strokeWidth="2.5"
                fill="none"
              />
              <rect
                x="14"
                y="12"
                width="36"
                height="24"
                rx="1"
                fill="currentColor"
                opacity="0.15"
              />
              <line
                x1="32"
                y1="42"
                x2="32"
                y2="52"
                stroke="currentColor"
                strokeWidth="2.5"
              />
              <line
                x1="22"
                y1="52"
                x2="42"
                y2="52"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
              {/* Power indicator dot */}
              <circle cx="32" cy="24" r="4" fill="currentColor" opacity="0.6" />
            </svg>
          </div>

          <div className="boot-title">SystemOS</div>

          <div className="boot-progress-track">
            <div
              className="boot-progress-fill"
              style={{ width: `${loaderProgress * 100}%` }}
            />
          </div>

          <div className="boot-hint">
            {phase !== "fadeout" && <span>Press any key to skip</span>}
          </div>
        </div>
      )}
    </div>
  );
}
